"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { MapPin, Globe, Compass, Map, PlaneTakeoff, Award, Cross, BookOpen, Youtube, ShoppingCart } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const destinations = [{
  id: "uk",
  name: "Purpose & Legacy",
  price: "14.99",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679939/img/path_through_woods.png",
  amazonUrl: "https://www.amazon.com/dp/B0GZ4472PX"
}, {
  id: "usa",
  name: "Foundations of Faith",
  price: "12.99",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679939/img/christ_related.png"
}, {
  id: "europe",
  name: "Bible Study Resources",
  price: "18.50",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679939/img/bible_study_group.png"
}, {
  id: "canada",
  name: "Rooted Disciples",
  price: "15.99",
  image: "https://giving-boa.10web.cloud/wp-content/uploads/2026/05/Untitled-design-4.png?v=9m59csxxv2b"
}];
const getDestinationIcon = (id: string) => {
  switch (id) {
    case "uk":
      return;
    case "usa":
      return;
    case "europe":
      return;
    case "canada":
      return;
    default:
      return <PlaneTakeoff className="size-10 text-accent mb-4 drop-shadow-md" />;
  }
};
export default function OurGlobalDestinations() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224928" id="the-library-collection" className="dark bg-background py-20 md:py-32 relative overflow-hidden">
      <div className="hidden md:block absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-accent/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerReveal.ref} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Badge variant="outline" className="mb-4 border-accent text-accent px-4 py-1 text-sm tracking-widest uppercase bg-accent/10 font-default">
            The Collection
          </Badge>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-6 tracking-tight">Resources for a Rooted Life</h2>
          <div className="w-24 h-1 bg-accent mx-auto mb-6 rounded-md" />
          <p className="font-default text-muted-foreground text-lg md:text-xl leading-relaxed">Helping people grow deeper in faith, live rooted in Christ, and build a legacy that points others to Him.  </p>
        </div>

        <div ref={gridReveal.ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-1000 delay-200 ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {destinations.map((dest, i) => <div key={dest.id} data-index={i} className="group relative h-[420px] w-full overflow-hidden rounded-md bg-card shadow-md transition-all duration-500 hover:shadow-xl hover:-translate-y-2 border border-border/50">
              <img src={dest.image} alt={`${dest.name} Book Cover`} className="absolute inset-0 h-full w-full object-cover transition-all duration-700 group-hover:scale-105" data-wvc-srcset={dest.image.includes('http') ? undefined : `${dest.image}-wvc-srcset`} data-wvc-sizes={dest.image.includes('http') ? undefined : `${dest.image}-wvc-sizes`} loading="lazy" />
              
              <div className="absolute inset-0 bg-primary/80 transition-colors duration-500 group-hover:bg-primary/60" />

              <div className={`absolute inset-0 z-10 flex flex-col items-center p-6 text-center ${i === 3 || i === 2 || i === 1 || dest.amazonUrl ? "justify-between h-full" : "justify-center"}`}>
                <div className="transition-transform duration-500 flex flex-col items-center w-full h-full justify-center">
                  {getDestinationIcon(dest.id)}
                  <h3 className="font-serif text-3xl font-bold text-white tracking-wide drop-shadow-lg mb-4">
                    {dest.name}
                  </h3>
                  <div className="flex flex-col gap-3">
                    {i === 1 && <Link to="https://www.amazon.com/dp/B0GX2WGNLY" newTab={true} className="inline-flex items-center gap-2 bg-amber-700 text-white px-6 py-2 rounded-md font-default text-sm font-semibold transition-all duration-300 hover:bg-amber-800 hover:scale-105 shadow-md">
                        <ShoppingCart className="size-4" />
                        Explore Collection on Amazon
                      </Link>}
                    {i === 2 && <Link to="https://www.amazon.com/dp/B0GX2ZCT5P" newTab={true} className="inline-flex items-center gap-2 bg-amber-700 text-white px-6 py-2 rounded-md font-default text-sm font-semibold transition-all duration-300 hover:bg-amber-800 hover:scale-105 shadow-md">
                        <ShoppingCart className="size-4" />
                        Explore Collection on Amazon
                      </Link>}
                    {i === 3 && <Link to="https://youtube.com/@RootedDisciples" newTab className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-2 rounded-md font-default text-sm font-semibold transition-all duration-300 hover:bg-red-700 hover:scale-105 shadow-md">
                        <Youtube className="size-4" />
                        Watch on YouTube
                      </Link>}
                    {dest.amazonUrl && <Link to={dest.amazonUrl} newTab={true} className="inline-flex items-center gap-2 bg-amber-700 text-white px-6 py-2 rounded-md font-default text-sm font-semibold transition-all duration-300 hover:bg-amber-800 hover:scale-105 shadow-md">
                        <ShoppingCart className="size-4" />
                        {i === 0 ? "Explore Collection on Amazon" : "Buy on Amazon"}
                      </Link>}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}